import * as React from 'react';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { Box, Button, Link } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { Widget } from '../Widget';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      width: 'fit-content',
      marginTop: theme.spacing(2),
      marginRight: theme.spacing(2),
      display: 'flex',
      flexDirection: 'row'
    },
    link: {
      fontSize: 16,
      lineHeight: '24px',
      letterSpacing: 0.15,
      fontWeight: theme.typography.fontWeightRegular,
      paddingTop: theme.spacing(1.5),
      marginBottom: theme.spacing(0.5),
      width: 'fit-content',
      display: 'flex',
      flexDirection: 'column'
    }
  })
);

type Permission = {
  resourceName: ActivityResourceName;
  action: ActivityAction;
};

export type Link = {
  label: string;
  url: string;
  permission?: Permission;
};

type RouteParams = {
  organizationId: string;
};

type Props = {
  links: Link[];
};

export const QuickLinks: React.FC<Props> = ({ links }) => {
  const classes = useStyles();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation('subscriber');
  const { hasGlobalPermission, hasOrganizationPermission } = usePermission();

  const getRedirectLink = (url: string): string => {
    return url[0] === '/' ? `/organizations/${organizationId}${url}` : url;
  };

  return (
    <Widget title={t('newDashboard.quickLinks.title', 'Actions')}>
      <Box display="flex" flexDirection="column">
        <Box display="flex" flexWrap="wrap">
          {links.map(({ label, url, permission }, index) => {
            let hasPermission = true;
            if (permission) {
              const { resourceName, action } = permission;
              hasPermission =
                hasGlobalPermission(action, resourceName) || hasOrganizationPermission(action, resourceName);
            }
            if (!hasPermission) return null;

            if (label === 'Help') {
              return (
                <Box key={index} width="100%" mt={2}>
                  <Link color="primary" href={getRedirectLink(url)} target="_blank" classes={{ root: classes.link }}>
                    {label}
                  </Link>
                </Box>
              );
            }
            return (
              <Button
                key={index}
                variant="contained"
                onClick={() => {
                  const redirectUrl = getRedirectLink(url);
                  window.open(redirectUrl, '_blank');
                }}
                className={classes.button}
                color="primary"
              >
                {label}
              </Button>
            );
          })}
        </Box>
      </Box>
    </Widget>
  );
};
